import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LpField from "../../../components/common/linchpin/LpField";
import LpInput from "../../../components/common/linchpin/LpInput";
import LpCheckbox from "../../../components/common/linchpin/LpCheckbox";
import LpAddressSearch from "../../../components/common/linchpin/LpAddressSearch";
import { Search } from "../../../components/common/linchpin/LpIcons";
import MobileInput_v2 from "../../../components/common/ReusableMobileInput";
import SavePredefinedModal from "./SavePredefinedModal";
import { resolveCompanyName } from "../../../helpers/address";
import "./AddressV2.css";

const splitName = (full) => {
  const raw = full ?? "";
  if (!raw) return { first: "", last: "" };
  const idx = raw.indexOf(" ");
  if (idx === -1) return { first: raw, last: "" };
  const first = raw.slice(0, idx);
  const last = raw.slice(idx + 1);
  // Preserve trailing space after first name while the user is still typing
  // (e.g. "Michael " before last name starts). `.trim()` used to strip it and
  // joinName would immediately drop the space from the controlled input.
  if (last === "" && raw.endsWith(" ")) {
    return { first: `${first} `, last: "" };
  }
  return { first, last };
};

const joinName = (first, last) => {
  const f = first ?? "";
  const l = last ?? "";
  if (!l) return f;
  return `${f.replace(/\s+$/, "")} ${l}`;
};

function AddressV2({
  location = {},
  onChangeDirect,
  onAddressSelect,
  orderTypeConfig,
  selectedWarehouseId,
  isLinehaulCode,
  isDstOrOrn,
  disabled = false,
  orgId,
  isMilitaryTime = false,
  // Fired with true/false when the "Save as Predefined" modal opens/closes.
  // The modal portals to <body> (outside the address block), so the parent
  // uses this to suppress its focus-out/outside-click collapse while the modal
  // is open — otherwise opening it folds (and unmounts) the address form.
  onSaveModalToggle,
}) {
  // Parent's `onChangeDirect`/`onAddressSelect` props get a fresh identity on
  // every parent render (the legacy `AddressLocationDetails` doesn't memoize
  // its inline closures). Stash them in refs so our per-field handlers stay
  // identity-stable — that lets the memoized LpInputs/LpAddressSearch skip
  // re-renders on every keystroke, which was the source of the typing lag.
  const onChangeDirectRef = useRef(onChangeDirect);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onSaveModalToggleRef = useRef(onSaveModalToggle);
  useEffect(() => { onChangeDirectRef.current = onChangeDirect; });
  useEffect(() => { onAddressSelectRef.current = onAddressSelect; });
  useEffect(() => { onSaveModalToggleRef.current = onSaveModalToggle; });

  // Build one stable change handler per text field, once. The handler reads
  // e.target.value synchronously (React 16 SyntheticEvent pooling) and routes
  // through the ref so it always uses the latest parent setter.
  const fieldHandlers = useMemo(() => {
    const make = (key) => (e) => {
      const v = e && e.target ? e.target.value : e;
      onChangeDirectRef.current(key, v);
    };
    const digitsOnly = (e) => {
      const v = e && e.target ? e.target.value : e;
      onChangeDirectRef.current("customer_zipcode", String(v || "").replace(/\D/g, ""));
    };
    return {
      company_name: make("company_name"),
      customer_address_line_1: make("customer_address_line_1"),
      customer_address_line_2: make("customer_address_line_2"),
      customer_city: make("customer_city"),
      customer_state: make("customer_state"),
      customer_zipcode: digitsOnly,
      customer_country: make("customer_country"),
      customer_email: make("customer_email"),
      notes: make("notes"),
    };
  }, []);

  const onContactNameChange = useCallback((e) => {
    const raw = e.target.value;
    const { first, last } = splitName(raw);
    onChangeDirectRef.current("customer_first_name", first);
    onChangeDirectRef.current("customer_last_name", last);
  }, []);

  const onPhone1Change = useCallback(
    (val) => onChangeDirectRef.current("customer_phone_one", val),
    []
  );
  const onPhone2Change = useCallback(
    (val) => onChangeDirectRef.current("customer_phone_two", val),
    []
  );

  const isResidential =
    typeof location.address_type === "string" &&
    ["RESIDENTIAL", "RESEDENTIAL"].includes(location.address_type.toUpperCase());
  const onResidentialToggle = useCallback((e) => {
    const checked = e.target.checked;
    onChangeDirectRef.current(
      "address_type",
      // Persist the app-wide canonical spelling. The backend, v2 UI and print
      // service all use the (misspelled) "RESEDENTIAL"; v3 originally wrote
      // "RESIDENTIAL", which those layers didn't recognize -> details screen
      // showed the raw value and the two screens disagreed for the same order.
      checked ? "RESEDENTIAL" : "COMMERCIAL"
    );
  }, []);

  // "Save as Predefined" needs enough address data to make a sensible
  // predefined-stop record. Gate the button on the BE-required-ish fields:
  // a name (company or contact), full street/city/state/zip/country.
  const trimmed = (v) => String(v == null ? "" : v).trim();
  const hasName =
    !!trimmed(location.company_name) ||
    !!trimmed(location.customer_first_name) ||
    !!trimmed(location.customer_last_name);
  const hasAddress =
    !!trimmed(location.customer_address_line_1) &&
    !!trimmed(location.customer_city) &&
    !!trimmed(location.customer_state) &&
    !!trimmed(location.customer_zipcode) &&
    !!trimmed(location.customer_country);
  const canSavePredefined = !disabled && hasName && hasAddress;

  const [showSaveModal, setShowSaveModal] = useState(false);
  const openSaveModal = useCallback(() => setShowSaveModal(true), []);
  const closeSaveModal = useCallback(() => setShowSaveModal(false), []);
  // Tell the parent to suppress its collapse-on-focus-out/outside-click while the
  // modal is open (it portals outside the address block).
  useEffect(() => {
    if (onSaveModalToggleRef.current) onSaveModalToggleRef.current(showSaveModal);
  }, [showSaveModal]);
  const handleSaved = useCallback((saved) => {
    // Persisted to the org's predefined list. `location_id` is the single
    // source of truth for "this leg references a predefined record" — used
    // by the button's pressed-state visual below and by the BE lookup.
    // Don't set a separate `is_predefined` flag here: that key is a BE
    // write-instruction (triggers a duplicate add_predefined_location
    // call in customer_orders flow), not a read-state indicator.
    const set = onChangeDirectRef.current;
    // set("is_predefined", true); as backend call is already made in createPredefinedStop
    if (saved?.location_code) set("location_code", saved.location_code);
    if (saved?.id) set("location_id", saved.id);
    if (saved?.name) set("location_name", saved.name);
  }, []);

  const contactNameValue = joinName(
    location.customer_first_name,
    location.customer_last_name
  );

  const [searchText, setSearchText] = useState("");

  // Enrich the picked record so the parent's mapper produces non-empty values
  // for fields the parent doesn't fall back on. Predefined warehouses ship
  // with `company_name: null` and `name: "AUS"`-style code, so the Name input
  // would otherwise stay blank after pick.
  const enrich = (item) => ({
    ...item,
    company_name: resolveCompanyName(item),
  });

  // All three address-pick callsites suppress the parent's modal-open side
  // effect (the inline form already shows what the modal would have shown —
  // the address block lives inside the leg card now). Read onAddressSelect
  // via ref so these handlers stay identity-stable.
  const pickFromTop = useCallback((item) => {
    // focusName: only the top Search Location pick moves focus to the Name field
    // afterward (the natural next field). Name/Street picks keep focus where it is.
    onAddressSelectRef.current(enrich(item), { openModal: false, focusName: true });
    setSearchText("");
  }, []);
  const pickFromName = useCallback(
    (item) => onAddressSelectRef.current(enrich(item), { openModal: false }),
    []
  );
  const pickFromStreet = useCallback(
    (item) => onAddressSelectRef.current(enrich(item), { openModal: false }),
    []
  );

  const onNameTextChange = useCallback(
    (v) => onChangeDirectRef.current("company_name", v),
    []
  );
  const onStreetTextChange = useCallback(
    (v) => onChangeDirectRef.current("customer_address_line_1", v),
    []
  );
  // "Add New '<typed>'" — the text is already saved to company_name via
  // onNameTextChange on every keystroke, so this just closes the dropdown
  // (which LpAddressSearch does internally after calling onAddNew).
  const onAddNewName = useCallback(
    (v) => onChangeDirectRef.current("company_name", v || ""),
    []
  );

  return (
    <div className="lp-v3 lav2-root">
      {/* Chrome ignores autocomplete="off" on large order forms — decoy fields
          absorb autofill before the real inputs are unlocked on focus. */}
      <input
        type="text"
        name="lav2_autofill_trap_user"
        className="lav2-autofill-trap"
        tabIndex={-1}
        autoComplete="username"
        aria-hidden="true"
      />
      <input
        type="password"
        name="lav2_autofill_trap_pw"
        className="lav2-autofill-trap"
        tabIndex={-1}
        autoComplete="new-password"
        aria-hidden="true"
      />
      <div className="lav2-search">
        <LpField label="Search Location">
          <LpAddressSearch
            value={searchText}
            onChange={setSearchText}
            name="lav2_location_search"
            onSelect={pickFromTop}
            mode={isDstOrOrn ? "name" : "both"}
            placeholder="Search name of company, location, or address"
            prefix={<Search width={16} height={16} />}
            orderTypeConfig={orderTypeConfig}
            selectedWarehouseId={selectedWarehouseId}
            linehaul_code={isLinehaulCode}
            linehaul_label={isDstOrOrn}
            disabled={disabled}
          />
        </LpField>
      </div>

      <LpField label="Name">
        <LpAddressSearch
          mode="name"
          value={location.company_name || ""}
          onChange={onNameTextChange}
          onSelect={pickFromName}
          showAddNew
          onAddNew={onAddNewName}
          name="lav2_company_name"
          placeholder="Company name or person name"
          orderTypeConfig={orderTypeConfig}
          selectedWarehouseId={selectedWarehouseId}
          linehaul_code={isLinehaulCode}
          linehaul_label={isDstOrOrn}
          disabled={disabled}
        />
      </LpField>

      <div className="lav2-row">
        <div className="lav2-col-3">
          <LpField label="Street Address" required>
            <LpAddressSearch
              mode={isDstOrOrn ? "none" : "address"}
              value={location.customer_address_line_1 || ""}
              onChange={onStreetTextChange}
              onSelect={pickFromStreet}
              name="lav2_street_address"
              placeholder="Street Address"
              orderTypeConfig={orderTypeConfig}
              selectedWarehouseId={selectedWarehouseId}
              linehaul_code={isLinehaulCode}
              linehaul_label={isDstOrOrn}
              disabled={disabled}
            />
          </LpField>
        </div>
        <div className="lav2-col-1">
          <LpField label="Suite, Apt, Etc">
            <LpInput
              value={location.customer_address_line_2 || ""}
              onChange={fieldHandlers.customer_address_line_2}
              placeholder="Suite, Apt, Etc"
              name="lav2_address_suite"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
      </div>

      <div className="lav2-row">
        <div className="lav2-col-1">
          <LpField label="City" required>
            <LpInput
              value={location.customer_city || ""}
              onChange={fieldHandlers.customer_city}
              placeholder="City"
              name="lav2_address_city"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
        <div className="lav2-col-1">
          <LpField label="State" required>
            <LpInput
              value={location.customer_state || ""}
              onChange={fieldHandlers.customer_state}
              placeholder="State"
              name="lav2_address_state"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
        <div className="lav2-col-1">
          <LpField label="Zip Code" required>
            <LpInput
              value={location.customer_zipcode || ""}
              onChange={fieldHandlers.customer_zipcode}
              onKeyPress={(e) => {
                if (!/^\d$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              inputMode="numeric"
              placeholder="Zip"
              name="lav2_address_zip"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
        <div className="lav2-col-1">
          <LpField label="Country" required>
            <LpInput
              value={location.customer_country || ""}
              onChange={fieldHandlers.customer_country}
              placeholder="Country"
              name="lav2_address_country"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
      </div>

      <div className="lav2-row">
        <div className="lav2-col-2">
          <LpField label="Contact Name">
            <LpInput
              value={contactNameValue}
              onChange={onContactNameChange}
              placeholder="First & Last Name"
              name="lav2_contact_name"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
        <div className="lav2-col-2">
          <LpField label="Contact Email">
            <LpInput
              type="email"
              value={location.customer_email || ""}
              onChange={fieldHandlers.customer_email}
              placeholder="Email Address"
              name="lav2_contact_email"
              disableBrowserAutofill
              disabled={disabled}
            />
          </LpField>
        </div>
      </div>

      <div className="lav2-row">
        <div className="lav2-col-2">
          <LpField label="Contact Primary Phone">
            <MobileInput_v2
              value={location.customer_phone_one || ""}
              handleInputChange={onPhone1Change}
              disabled={disabled}
              linchpin
              wrapperClassName="lav2-phone"
              placeholder="Phone number"
              name="lav2_contact_phone_primary"
              disableBrowserAutofill
            />
          </LpField>
        </div>
        <div className="lav2-col-2">
          <LpField label="Contact Secondary Phone">
            <MobileInput_v2
              value={location.customer_phone_two || ""}
              handleInputChange={onPhone2Change}
              disabled={disabled}
              linchpin
              wrapperClassName="lav2-phone"
              placeholder="Phone number"
              name="lav2_contact_phone_secondary"
              disableBrowserAutofill
            />
          </LpField>
        </div>
      </div>

      <LpField label="Location Notes">
        <LpInput
          value={location.notes || ""}
          onChange={fieldHandlers.notes}
          placeholder="Enter notes about the location"
          disabled={disabled}
        />
      </LpField>

      <div className="lav2-actions">
        <LpCheckbox
          label="Residential Address"
          checked={isResidential}
          onChange={onResidentialToggle}
          disabled={disabled}
        />
        <button
          type="button"
          className="lav2-save-predef"
          onClick={openSaveModal}
          disabled={!canSavePredefined}
          aria-pressed={!!location.location_id}
          title={canSavePredefined ? undefined : "Fill name and address first"}
        >
          Save as Predefined
        </button>
      </div>
      <SavePredefinedModal
        visible={showSaveModal}
        onCancel={closeSaveModal}
        onSaved={handleSaved}
        location={location}
        orgId={orgId}
        isMilitaryTime={isMilitaryTime}
      />
    </div>
  );
}

export default React.memo(AddressV2);
